import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Divider,
  Stack,
  Chip,
  Checkbox,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  Paper,
} from '@mui/material';
import MaterialSymbol from '../../components/MaterialSymbol';
import { IconButton, Collapse } from '@mui/material';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AddCircleIcon from '@mui/icons-material/AddCircle';
// import SendIcon from '@mui/icons-material/Send';

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import MemberOnboardModal from '../../components/MemberOnboardModal';
import MemberRenewModal from '../../components/MemberRenewModal';
import MemberPaymentConfirmationModal from '../../components/MemberPaymentConfirmationModal';
import MemberJourneyModal from '../../components/MemberJourneyModal';
import AssignMultipleModal from '../../components/AssignMultipleModal';
import UploadModal from '../../components/UploadModal';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { ProfileAvatar } from '../../components/DynamicForm';
import { humanReadableDate, titleCase, fitnessMembershipStatusColor, fitnessMembershipStatusText } from '../../common/AppUtils'
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL, SEND_INVITE_URL } from '../../common/AppConstants';

const TITLE = 'Members';
const MEMBER_API_URL = 'fitness-members/member/';
const ONBOARD_API_URL = 'fitness-memberships/onboard/';
const RENEW_API_URL = 'fitness-memberships/renew/';
const MEMBER_JOURNEY_API_URL = 'fitness-memberships/journey/';
const UPLOAD_TEMPLATE = 'member_upload_template.xlsx';  // legacy
const INVITE_TYPE = 'fitness_member';
const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';

const sendInviteColumns = [
  {
    attr: 'email',
    editable: false,
    extra: false,
    label: 'Email',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Send invite at email',
  },
];

export default function Members() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [onboardConfirmation, setOnboardConfirmation] = useState(null);

  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [selectedRenewRow, setSelectedRenewRow] = useState(null);
  const [renewConfirmation, setRenewConfirmation] = useState(null);

  const [isJourneyModalOpen, setIsJourneyModalOpen] = useState(false);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyData, setJourneyData] = useState(null);

  const filteredRows = rows.filter((row_item) => {
    const subscription = row_item.current_membership?.current_subscription;

    const statusText = subscription
      ? fitnessMembershipStatusText(
          subscription.status,
          subscription.date_status,
          subscription.is_current
        )
      : '';

    const search = searchTerm.toLowerCase();

    return (
      row_item.full_name?.toLowerCase().includes(search) ||
      statusText.toLowerCase().includes(search) ||
      row_item.email?.toLowerCase().includes(search) ||
      row_item.phone?.toLowerCase().includes(search)
    );
  });

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleEditClick = (row) => {
    setSelectedEditRow(row);
  };

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]); // Runs when selectedEditRow changes

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: MEMBER_API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row, index) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: MEMBER_API_URL,
      data: deletedRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.filter((row) => row.id !== deletedRow.id),
        );
      },
      addNotification,
    });
  };

  const handleAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    await httpRequest({
      method: 'POST',
      url: ONBOARD_API_URL,
      data: addFormData,
      onSuccess: (data) => {
        setRows((prevRows) => [data, ...prevRows]);
        onAddSuccess();
        handleAddModalClose();
        // handleAddClick();
        setOnboardConfirmation(data);
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });
  };

  const handleUpload = async (file, onUploadSuccess, onUploadFailure) => {
    const formData = new FormData();
    formData.append('file', file);

    await httpRequest({
      method: 'POST',
      url: `${MEMBER_API_URL}upload/`,
      data: formData,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      onSuccess: (data) => {
        refreshData();
        onUploadSuccess(data);
      },
      onFailure: (data) => {
        onUploadFailure(data);
      },
      addNotification,
    });
  };

  const handleRenewClick = (member_dict) => {
    setSelectedRenewRow(member_dict);
    setIsRenewModalOpen(true);
  };

  const handleRenewModalClose = () => {
    setIsRenewModalOpen(false);
    setSelectedRenewRow(null);
  };

  const handleRenewSave = async (formData, onSuccess, onFailure) => {
    const finalFormData = {
      ...formData,
      member: selectedRenewRow.id,
    };
    await httpRequest({
      method: 'POST',
      url: RENEW_API_URL,  // or a dedicated renew URL
      data: finalFormData,
      onSuccess: (data) => {
        setRows(prevRows =>
          prevRows.map(row => row.id === selectedRenewRow.id ? { ...row, ...data } : row)
        );
        onSuccess();
        handleRenewModalClose();
        setRenewConfirmation(data);
      },
      onFailure: (data) => {
        onFailure(data);
      },
      addNotification,
    });
  };

  const handleJourneyClick = async (member_dict) => {
    setJourneyLoading(true);
    setIsJourneyModalOpen(true);
    await httpRequest({
      method: 'GET',
      url: MEMBER_JOURNEY_API_URL,
      data: {id: member_dict?.id},
      onSuccess: (data) => {
        setJourneyData({...member_dict, ...data});
      },
      onFailure: (data) => {
      },
      addNotification,
    });
    setJourneyLoading(false);
  };

  const handleJourneyModalClose = () => {
    setIsJourneyModalOpen(false);
    setJourneyData(null);
  };

  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      fetchOnboardMeta();
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: MEMBER_API_URL,
      onSuccess: (data) => {
        setColumns(data.columns);
        setRows(data.rows);
      },
      onFailure: (data) => {
        setColumns([]);
        setRows([]);
      },
      addNotification,
    });
    
    setIsLoading(false);
  };

  const fetchOnboardMeta = async (event) => {
    await httpRequest({
      method: 'GET',
      url: ONBOARD_API_URL,
      onSuccess: (data) => {
        setRelatedData(data);
      },
      addNotification,
    });
  };

  const MemberCard = ({ member_row, member_dict, relatedData, onRenewClick, onJourneyClick }) => {
    // const incomplete_data = member_dict['subject_list'].length === 0;  // && !member_dict.can_teach_all_subjects
    const [expanded, setExpanded] = useState(false);  // incomplete_data
    const [isSendInviteModalOpen, setIsSendInviteModalOpen] = useState(false);

    const membership = member_dict?.current_membership;
    const current_subscription = membership?.current_subscription;

    const chipColor = fitnessMembershipStatusColor(current_subscription?.status, current_subscription?.date_status, current_subscription?.is_current);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };


    const handleSendInviteModalOpen = () => {
      setIsSendInviteModalOpen(true);
    };

    const handleSendInviteModalClose = () => {
      setIsSendInviteModalOpen(false);
    };

    const handleSendInvite = async (row_dict, onEditFailure) => {
      if (row_dict.verified) {
        addNotification("Already joined", 'info');
        return;
      }

      if (!row_dict.email) {
        addNotification("Please add email address to send invitation", 'warning');
        return;
      }

      const data = {
        invite_type: INVITE_TYPE,
        profile_id: row_dict.id,
      };
      await httpRequest({
        method: 'POST',
        url: SEND_INVITE_URL,
        data: data,
        onSuccess: (data) => {
          handleSendInviteModalClose();
        },
        addNotification,
      });
    };

    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'box-shadow 0.3s',
          '&:hover': { boxShadow: 3 }
        }}
      >
        {/* Header */}
        <CardHeader
          avatar={
            <ProfileAvatar dataDict={member_dict} handleSendInviteModalOpen={handleSendInviteModalOpen} />
          }
          title={
            <Typography variant="h5">{member_dict['full_name']}</Typography>
          }
          action={
            <IconButton onClick={() => handleEditClick(member_dict)} size="small">
              <EditTwoToneIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            px: 2, py: 0, alignItems: 'center',
            '& .MuiCardHeader-action': { alignSelf: 'center' },
          }}
          slotProps={{
            content: { sx: { py: 1.5 } },
          }}
        />

        <Divider />

        {/* Main Content */}
        <CardContent sx={{ pt: 1.5, pb: 1, flexGrow: 1 }}>
          {current_subscription ? (
            <Stack spacing={0.75}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Box display="flex" alignItems="center" gap={0.75}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: chipColor }} />
                  <Typography variant="body2" fontWeight="bold" sx={{ color: chipColor }}>{fitnessMembershipStatusText(current_subscription?.status, current_subscription?.date_status, current_subscription?.is_current)}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Plan</Typography>
                <Typography variant="body2">{current_subscription.plan_name ?? NA_LABEL}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Start date</Typography>
                <Typography variant="body2">{humanReadableDate(current_subscription.start_date) ?? NA_LABEL}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Valid till</Typography>
                <Typography variant="body2">{humanReadableDate(current_subscription.end_date) ?? NA_LABEL}</Typography>
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">No active subscription</Typography>
          )}
          
          {/* Renewal Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end', // key
              alignItems: 'center',
              gap: 1.25,                     // spacing between buttons
              py: 0.75,
            }}
          >
            {/* <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => addNotification('Not allowed !!!', "warning")}
              sx={{ minHeight: 20, fontSize: '0.75rem' }}
              startIcon={<PersonRemoveIcon />}
            >
              Remove
            </Button> */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => onJourneyClick(member_dict)}
              sx={{ minHeight: 20, fontSize: '0.75rem' }}
              startIcon={<MaterialSymbol name="flowsheet" size={20} fill={1} />}
            >
              Journey
            </Button>

            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => onRenewClick(member_dict)}
              sx={{ minHeight: 20, fontSize: '0.75rem' }}
              startIcon={<AutorenewIcon />}
            >
              Renew
            </Button>
          </Box>
        </CardContent>

        {/* Collapsible Content */}
        <Collapse in={expanded}>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <CardContent sx={{ bgcolor: '#FBFBFB', pt: 1.5, pb: 1 }}>
            <Stack spacing={0.75}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Gender</Typography>
                <Typography variant="body2">{titleCase(member_dict['gender']) ?? NA_LABEL}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body2">{member_dict['email'] ?? NA_LABEL}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Phone</Typography>
                <Typography variant="body2">{member_dict['phone'] ?? NA_LABEL}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Joined</Typography>
                <Typography variant="body2">{humanReadableDate(membership.joined_at) ?? NA_LABEL}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Collapse>

        {/* Expand toggle — full width bottom */}
        {/* <Divider /> */}
        <Box
          onClick={handleExpandClick}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.25,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <KeyboardDoubleArrowDownIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </Box>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {/* <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              sx={{
                px: 4,
                display: { md: 'flex', xs: 'none' },
              }}
            >
              Upload
            </Button> */}
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top,
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <MemberCard
                      member_row={index}
                      member_dict={item_dict}
                      relatedData={{}}
                      onRenewClick={handleRenewClick}
                      onJourneyClick={handleJourneyClick}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Onboard Member Modal */}
      <Box>
        <MemberOnboardModal
          open={isAddModalOpen}
          plans={relatedData?.regular_plans}
          onClose={handleAddModalClose}
          onSave={handleAddSave}
        />
      </Box>

      {/* Member Onboard Confirmation Modal */}
      <MemberPaymentConfirmationModal
        open={Boolean(onboardConfirmation)}
        onClose={() => setOnboardConfirmation(null)}
        data={onboardConfirmation}
        modal_title="Member added"
      />

      {/* Membership Renewal Modal */}
      <MemberRenewModal
        open={isRenewModalOpen}
        member={selectedRenewRow}
        plans={relatedData?.regular_plans}
        onClose={handleRenewModalClose}
        onSave={handleRenewSave}
      />

      {/* Membership Renewal Confirmation Modal */}
      <MemberPaymentConfirmationModal
        open={Boolean(renewConfirmation)}
        onClose={() => setRenewConfirmation(null)}
        data={renewConfirmation}
        modal_title="Membership renewed"
      />

      {/* Member Jounrey Modal */}
      <MemberJourneyModal
        open={isJourneyModalOpen}
        data={journeyData}
        loading={journeyLoading}
        onClose={handleJourneyModalClose}
      />

      {/* Upload Modal */}
      <UploadModal
        open={isUploadModalOpen}
        uploadTemplate={UPLOAD_TEMPLATE}
        handleClose={handleUploadModalClose}
        handleUpload={handleUpload}
      />

      {/* Edit Member Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={columns}
          rowData={selectedEditRow}
          relatedData={{}}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Member"
        />
      </Box>
    </Box>
  );
}
